import {
  AppShell,
  FilterSheet,
  MapTemplate,
  MapTemplateSkeleton,
  TopBarSkeleton,
} from '@fontbold/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppTabBar, AppTopBar } from '#/components/AppChrome';
import { AppNewsletter, openNewsletter } from '#/components/AppNewsletter';
import {
  activeChips,
  buildSections,
  EMPTY_FILTERS,
  type FilterablePlace,
  type FilterGroupKey,
  type FilterState,
  hasAnyFilter,
  matchesFilters,
  parseFilters,
  serializeFilters,
  toggleValue,
} from '#/lib/filters';
import { type CitySummary, createLocationLabels } from '#/lib/location';
import { locationSummaryQueries } from '#/lib/location-summary';
import { placeMapQueries } from '#/lib/place-map';
import { loadMapPreview } from '#/lib/place-preview';
import { embedMeta } from '#/lib/placeholder-data';
import type { Space } from '#/lib/queries/space';
import { pageTitle } from '#/lib/site';
import { getSpace } from '#/lib/space-fn';
import { spaceLogo } from '#/lib/space-logo';
import {
  FILTER_SHEET_TRANSLATIONS,
  MAP_TRANSLATIONS,
} from '#/lib/translations';
import { allPlacesQueries } from './-queries';

type MapMarker = {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  href?: string;
};

type MapPlaceRow = FilterablePlace & {
  name: string;
  slug: string;
  href: string;
  area: string;
  areaShort: string;
  cover: string;
};

type LoaderData = {
  space: Space | null;
  markers: MapMarker[];
  places: MapPlaceRow[];
  cities: CitySummary[];
  tags: { slug: string; label: string }[];
};

const LIST_LIMIT = 60;

export const Route = createFileRoute('/kaart')({
  validateSearch: (search: Record<string, unknown>) =>
    search as Record<string, string>,
  loader: async ({ context }): Promise<LoaderData> => {
    const space = await getSpace();
    if (!space)
      return { space: null, markers: [], places: [], cities: [], tags: [] };

    const cities = await context.queryClient.ensureQueryData(
      locationSummaryQueries.cities(space.id),
    );
    const [markers, result] = await Promise.all([
      context.queryClient.ensureQueryData(placeMapQueries.all(space.id)),
      context.queryClient.ensureQueryData(
        allPlacesQueries.places(space.id, 100, createLocationLabels(cities)),
      ),
    ]);

    return {
      space,
      markers,
      tags: result.tags,
      places: result.places.map((place) => ({
        id: place.id,
        name: place.name,
        slug: place.slug,
        citySlug: place.citySlug,
        districtSlug: place.districtSlug,
        tagSlugs: Array.from(new Set(place.labelSlugs)),
        href: place.href,
        area: [place.districtLabel, place.cityLabel].filter(Boolean).join(', '),
        areaShort: place.districtLabel || place.cityLabel,
        cover: place.poster,
      })),
      cities,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: pageTitle('Kaart', loaderData?.space) }],
  }),
  pendingComponent: () => (
    <AppShell topBar={<TopBarSkeleton />} tabBar={null} fullBleed>
      <MapTemplateSkeleton />
    </AppShell>
  ),
  component: MapPage,
});

function MapPage() {
  const { space, markers, places, cities, tags } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activePlaceId, setActivePlaceId] = useState<string | null>(null);

  const q = typeof search.q === 'string' ? search.q.trim() : '';
  const [sheetQuery, setSheetQuery] = useState(q);

  const state = useMemo(() => parseFilters(search), [search]);
  const setState = (next: FilterState, nextQuery = q) =>
    navigate({
      search: {
        ...serializeFilters(next),
        q: nextQuery || undefined,
      } as Record<string, string>,
      replace: true,
    });

  const active = hasAnyFilter(state) || q.length > 0;
  const filteredPlaces = useMemo(() => {
    const needle = q.toLowerCase();
    return places.filter(
      (place) =>
        (!hasAnyFilter(state) || matchesFilters(place, state)) &&
        (!needle || place.name.toLowerCase().includes(needle)),
    );
  }, [places, state, q]);

  if (!space) return null;

  const count = filteredPlaces.length;
  const logo = spaceLogo(space);

  const visibleIds = new Set(filteredPlaces.map((place) => place.id));
  const visibleMarkers = active
    ? markers.filter((marker) => visibleIds.has(marker.id))
    : markers;

  const results = filteredPlaces.slice(0, LIST_LIMIT).map((place) => {
    const meta = embedMeta(place.id);
    return {
      id: place.id,
      name: place.name,
      href: place.href,
      area: place.area,
      areaShort: place.areaShort,
      meta: `Reel van ${meta.ago} geleden - ${meta.views} views`,
      cover: place.cover,
    };
  });

  const handlers = {
    onToggleChoice: (sectionId: string, slug: string) => {
      const key = sectionId as FilterGroupKey;
      setState({ ...state, [key]: toggleValue(state[key], slug) });
    },
    onSelectSegment: () => {},
    onToggleSwitch: () => {},
    onApplyRecent: () => {
      const firstCity = cities[0]?.slug;
      setState({ ...EMPTY_FILTERS, city: firstCity ? [firstCity] : [] });
    },
  };

  const clearFilters = () => setState(EMPTY_FILTERS, '');
  const sections = buildSections(places, state, cities, tags);

  const qChip = q
    ? [
        {
          id: 'q',
          label: `"${q}"`,
          onRemove: () => setState(state, ''),
        },
      ]
    : [];

  const lowResults =
    active && count > 0 && count <= 4
      ? {
          message: 'Weinig plekken binnen je selectie.',
          widenLabel: 'Filters versoepelen',
          onWiden: clearFilters,
        }
      : undefined;

  return (
    <>
      <MapTemplate
        topBar={
          <AppTopBar
            space={space}
            active="kaart"
            onNewsletter={openNewsletter}
            searchValue={q}
          />
        }
        tabBar={<AppTabBar space={space} active="kaart" />}
        translations={{
          ...MAP_TRANSLATIONS,
          listTitle: active ? 'Resultaten' : 'Dichtbij',
          resultCount: `${count} ${count === 1 ? 'plek' : 'plekken'}`,
        }}
        mapPlaces={visibleMarkers}
        activePlaceId={activePlaceId}
        onPinClick={setActivePlaceId}
        curatorLogo={logo}
        loadPreview={(marker) => loadMapPreview(space.id, marker.href ?? '')}
        filterSections={sections}
        filterHandlers={handlers}
        onClearFilters={clearFilters}
        quickActions={[
          {
            id: 'saved',
            label: 'Bewaard',
            onClick: () => navigate({ to: '/bewaard' }),
          },
        ]}
        activeChips={[
          ...qChip,
          ...activeChips(state, cities, tags).map((chip) => ({
            id: chip.id,
            label: chip.label,
            onRemove: () => {
              const slug = chip.id.split(':')[1] ?? '';
              setState({
                ...state,
                [chip.group]: toggleValue(state[chip.group], slug),
              });
            },
          })),
        ]}
        lowResults={lowResults}
        results={results}
        onOpenSearch={() => {
          setSheetQuery(q);
          setSheetOpen(true);
        }}
      />

      <FilterSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        searchValue={sheetQuery}
        onSearchChange={setSheetQuery}
        onSubmit={() => {
          setState(state, sheetQuery.trim());
          setSheetOpen(false);
        }}
        nearby={results.slice(0, 3).map((result) => ({
          id: result.id,
          name: result.name,
          meta: result.areaShort,
          distance: '',
          href: result.href,
        }))}
        sections={sections}
        handlers={handlers}
        resultCount={count}
        lowResults={lowResults}
        translations={FILTER_SHEET_TRANSLATIONS}
        onClear={clearFilters}
      />

      <AppNewsletter spaceId={space.id} logo={logo} />
    </>
  );
}
